import { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OwenerTransferModel } from 'ts/transfer/classes/OwenerTransferModel';
import { ownerValidation } from 'constants/transfer';
import Button from 'components/ui/Button/Button';
import { validate } from 'utils/validation';
import { BookingParams, pay } from 'services/transfer';
import { useSearchParams } from 'react-router-dom';
import { BookingModel } from 'ts/transfer/interfaces/TransferResultsResponse';
import PassengerForm from './PassengerForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCar, faUser } from '@fortawesome/free-solid-svg-icons';
import InfoForm from './InfoForm';
import OrderTripCompany from 'components/shared/OrderTripCompany';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface TransferBookingFormProps {
  bookingModel: BookingModel;
  onBookConinue: (model: BookingModel) => void;
  model?: OwenerTransferModel;
  companyId: string;
  tripId: string;
  setCompanyId: (companyId: string) => void;
  setTripId: (tripId: string) => void;
}

const TransferBookingForm: FC<TransferBookingFormProps> = (props) => {
  const [t] = useTranslation();
  const [urlParams] = useSearchParams();
  const [model, setModel] = useState(new OwenerTransferModel());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bModel, setBModel] = useState<BookingModel>(props.bookingModel);

  useEffect(() => {
    if (props.model) {
      setModel(props.model);
    }
  }, [props.model]);

  const onClickBook = async () => {
    setIsSubmitted(true);
    if (validate(ownerValidation, model)) {
      setLoading(true);
      const urlParmasObj = Object.fromEntries(urlParams.entries()) as unknown as BookingParams;
      const params = {
        ...urlParmasObj,
        owner: model,
      };
      const data = await pay(params);

      if (data && !data?.hasErrors) {
        await props.onBookConinue({
          ...data?.bookingModel,
          forwardDropoff: bModel.forwardDropoff,
          returnDropoff: bModel.returnDropoff,
          forwardPickup: bModel.forwardPickup,
          returnPickup: bModel.returnPickup,
          credit2000PaymentInfo: data.bookingModel.credit2000PaymentInfo,
        });
      }

      setLoading(false);
    }
  };

  return (
    <div className="transfer-results-data">
      <div className="book-form default-shadow mb-15">
        <div className="title-underline title-header-grey text-lg mb-5">
          {t('transfer.details')}
        </div>
        <div className="color-danger mb-10 text-xsm">{t('only_english_input')}</div>
        <div className="text-contrast text-bold text-md mb-10">
          <FontAwesomeIcon icon={faUser} size="xs" /> {t('passenger.details')}
        </div>
        <div className="mb-30 pb-20 divider">
          <PassengerForm model={model} onChangeModel={setModel} isSubmitted={isSubmitted} />
        </div>
        <div className="text-contrast text-bold text-md mb-10">
          <FontAwesomeIcon icon={faCar} size="xs" /> {t('transfer.arrival_info')}
        </div>
        <div className="ph-10 mb-30 pb-20">
          <InfoForm
            bookingModel={props.bookingModel}
            isSubmitted={isSubmitted}
            type="forward"
            onChange={setBModel}
          />
        </div>
        {!!props.bookingModel.return && (
          <Fragment>
            <div className="text-contrast text-bold text-md mb-10">
              <FontAwesomeIcon icon={faCar} size="xs" /> {t('transfer.return_info')}
            </div>
            <div className="ph-10">
              <InfoForm
                bookingModel={props.bookingModel}
                isSubmitted={isSubmitted}
                type="return"
                onChange={setBModel}
              />
            </div>
          </Fragment>
        )}

        <OrderTripCompany
          companyId={props.companyId}
          tripId={props.tripId}
          onChangeCompanyId={props.setCompanyId}
          onChangeTripId={props.setTripId}
        />
      </div>
      <div className="flex-row-end">
        <Button
          className="btn btn-primary btn-lg mb-15"
          title={t('transfer.continue_book')}
          onClick={onClickBook}
          disabled={loading}
        />
      </div>
    </div>
  );
};

export default withErrorBoundary(TransferBookingForm, 'TransferBookingForm');
